/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

// src/views/Dashboard/Hesaplar.js

import React, { useState, useMemo, useCallback } from "react";
import { DataTable } from "components/DataTable";
import FirmaEkleModal from "components/Modals/Firmalar/FirmaEkleModal";
import AracEkleModal from "components/Modals/Araclar/AracEkleModal"; 
import HesapIslemleriModal from "components/Modals/Firmalar/HesapIslemleriModal";
import { sortTableData } from "utils/sort";
import { getAccountsColumns } from "components/DataTable/accountColumns";
import { useHistory } from "react-router-dom";
import { useUserRole } from "hooks/useUserRole";
import { useColumnOrder } from "hooks/useColumnOrder";
import { useUser } from "contexts/UserContext";
import { useFiltersRef } from "hooks/useFiltersRef";
import { CheckCircleIcon, CloseIcon } from "@chakra-ui/icons";
import { FiRefreshCw, FiPlus } from "react-icons/fi";
import {
  Progress,
  VStack,
  Text,
  HStack,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Textarea,
  FormControl,
  FormLabel,
  Box,
  Divider,
  Checkbox,
  CheckboxGroup,
  Input,
  Spinner,
} from "@chakra-ui/react";
import FirmalarContextMenu from "components/ContextMenu/FirmalarContextMenu";
import FirmaDetayModal from "components/Modals/Firmalar/FirmaDetayModal";
import FirmaUetdsModal from "components/Modals/Firmalar/FirmaUetdsModal";
import DegisiklikGecmisiModal from "components/Modals/Firmalar/DegisiklikGecmisiModal";
import FirmaNotEkleModal from "components/Modals/Firmalar/FirmaNotEkleModal";
import HatirlatmaGonderModal from "components/Modals/Abonelik/HatirlatmaGonderModal";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";
import FilterPanel from "components/FilterPanel/FilterPanel";
import { useBayiOptions } from "hooks/useBayiOptions";
import { useFirmOptions } from "hooks/useFirmOptions";
import { formatFirmaName } from "utils/formatFirmaName";

// FilterPanel / FirmSelectorWithFilters ile aynı Türkçe arama normalizasyonu
const normalizeSearchText = (value = "") =>
  String(value)
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .trim();

export default function Hesaplar() {
  const history = useHistory();
  const toast = useToast();
  const { handleError } = useErrorHandler(); // Merkezi error handling
  const userRole = useUserRole();
  const { userBayiTipi } = useUser();
  
  // Filtre state (kullanıcının yazdığı/seçtiği değerler)
  const [firmaAdiFilter, setFirmaAdiFilter] = useState("");
  const [telefonFilter, setTelefonFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [uetdsFilter, setUetdsFilter] = useState("");
  const [bayiFilter, setBayiFilter] = useState("");
  
  // Uygulanan filtreler (Listele butonuna basınca bu değerler kullanılacak)
  const [appliedFirmaAdiFilter, setAppliedFirmaAdiFilter] = useState("");
  const [appliedTelefonFilter, setAppliedTelefonFilter] = useState("");
  const [appliedStatusFilter, setAppliedStatusFilter] = useState("");
  const [appliedTypeFilter, setAppliedTypeFilter] = useState("");
  const [appliedUetdsFilter, setAppliedUetdsFilter] = useState("");
  const [appliedBayiFilter, setAppliedBayiFilter] = useState("");
  
  // Arama yapılıp yapılmadığını takip et
  const [hasSearched, setHasSearched] = useState(false);

  // Bayiler hook'u sadece admin ve teknik_eleman için aktif (yetkili bayi ve saas bayi göremez)
  const shouldLoadBayiler = userRole === "admin" || userRole === "teknik_eleman";
  const {
    bayiler,
    loading: bayilerLoading,
  } = useBayiOptions(shouldLoadBayiler);
  
  const {
    firms,
    loading: firmsLoading,
  } = useFirmOptions();

  // Filtreleri ref ile tut - closure sorununu çözmek için
  const filtersRef = useFiltersRef({
    firmaAdiFilter,
    telefonFilter,
    statusFilter,
    typeFilter,
    uetdsFilter,
    bayiFilter,
  });

  // userBayiTipi artık Context'ten geliyor, ayrı API çağrısına gerek yok

  // Role göre kolonları filtrele
  const columns = useMemo(() => {
    return getAccountsColumns(userRole);
  }, [userRole]);
  const [columnOrder, setColumnOrder] = useColumnOrder(columns);

  const [sortColumn, setSortColumn] = useState("id");
  const [sortOrder, setSortOrder] = useState("asc");
  const [accounts, setAccounts] = useState([]);
  const [abonelikler, setAbonelikler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isBulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [selectedBayiIds, setSelectedBayiIds] = useState([]); // Admin: listede bayi daraltma
  const [bulkUetdsOnly, setBulkUetdsOnly] = useState(false);
  const [bulkCandidates, setBulkCandidates] = useState([]);
  const [bulkCandidatesLoading, setBulkCandidatesLoading] = useState(false);
  const [selectedFirmaIds, setSelectedFirmaIds] = useState([]);
  const [bulkAccountSearch, setBulkAccountSearch] = useState("");
  const [pasifModal, setPasifModal] = useState({
    isOpen: false,
    firma: null,
    reason: "",
    submitting: false,
  });

  // context menu states
  const [context, setContext] = useState({ position: null, row: null });
  const [selectedFirma, setSelectedFirma] = useState(null);

  // modals
  const [isEkleOpen, setEkleOpen] = useState(false);
  const [isAracEkleOpen, setAracEkleOpen] = useState(false);
  const [isHesapIslemleriOpen, setHesapIslemleriOpen] = useState(false);
  const [isDegisiklikGecmisiOpen, setIsDegisiklikGecmisiOpen] = useState(false);
  const [isNotEkleModalOpen, setIsNotEkleModalOpen] = useState(false);
  const [selectedFirmaForArac, setSelectedFirmaForArac] = useState(null);
  const [selectedFirmaForHesapIslemleri, setSelectedFirmaForHesapIslemleri] = useState(null);
  const [selectedFirmaForGecmis, setSelectedFirmaForGecmis] = useState(null);
  const [selectedFirmaForNot, setSelectedFirmaForNot] = useState(null);
  const [hatirlatmaModal, setHatirlatmaModal] = useState({
    isOpen: false,
    abonelik: null,
  });
  const [firmaUetds, setFirmaUetds] = useState(null);

  const loadAccounts = useCallback((applyFilters = false, bayiFilterOverride = null) => {
    // Eğer filtreler uygulanacaksa (Listele butonuna basınca)
    if (applyFilters) {
      // Ref'ten güncel filtreleri al
      const currentFilters = filtersRef.current;
      
      // Firma seçimi özel durumu: "Hepsi" seçildiğinde (value="all") bu geçerli bir filtre olarak kabul edilir
      // Böylece tüm firmaları listeleyebiliriz
      // "Hepsi" seçildiğinde (value="all") geçerli bir filtre olarak kabul edilir
      const firmaSecildi = currentFilters.firmaAdiFilter && currentFilters.firmaAdiFilter !== "";
      
      // En az bir filtre seçilmiş olmalı
      const hasAnyFilter = 
        firmaSecildi || // Firma seçimi yapıldıysa (Tümü dahil) geçerli
        (currentFilters.telefonFilter && currentFilters.telefonFilter.trim()) ||
        (currentFilters.statusFilter && currentFilters.statusFilter.trim()) ||
        (currentFilters.typeFilter && currentFilters.typeFilter.trim()) ||
        (currentFilters.uetdsFilter && currentFilters.uetdsFilter.trim()) ||
        (currentFilters.bayiFilter && currentFilters.bayiFilter.trim());
      
      if (!hasAnyFilter) {
        toast({
          title: "Uyarı",
          description: "Lütfen en az bir filtre seçin.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      setAppliedFirmaAdiFilter(currentFilters.firmaAdiFilter);
      setAppliedTelefonFilter(currentFilters.telefonFilter);
      setAppliedStatusFilter(currentFilters.statusFilter);
      setAppliedTypeFilter(currentFilters.typeFilter);
      setAppliedUetdsFilter(currentFilters.uetdsFilter);
      setAppliedBayiFilter(currentFilters.bayiFilter);
      setHasSearched(true);
    }
    
    setLoading(true);
    
    // Bayi filtresi için query parametresi oluştur
    // Yenile butonunda mevcut uygulanan filtreleri kullan, Listele butonunda yeni filtreleri kullan
    const bayiFilterToUse = bayiFilterOverride !== null 
      ? bayiFilterOverride 
      : (applyFilters ? filtersRef.current.bayiFilter : appliedBayiFilter);
    
    const params = new URLSearchParams();
    if (bayiFilterToUse && userRole === "admin") {
      // "ixpilot" seçildiğinde özel bir değer gönder (backend'de null olarak yorumlanacak)
      if (bayiFilterToUse === "ixpilot") {
        params.append("bayi_id", "null");
      } else {
        params.append("bayi_id", bayiFilterToUse);
      }
    }
    
    const firmalarUrl = `/api/firmalar${params.toString() ? `?${params.toString()}` : ""}`;
    
    // Firmalar ve abonelikler paralel olarak yükle
    Promise.all([
      api.get(firmalarUrl).then(res => res.json()),
      api.get("/api/abonelikler/").then(res => res.json())
    ])
    .then(([firmaData, abonelikData]) => {
      setAccounts(Array.isArray(firmaData) ? firmaData : []);
      setAbonelikler(Array.isArray(abonelikData) ? abonelikData : []);
    })
    .catch((error) => {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(error, "Hesaplar yükleme hatası");
      setAccounts([]);
      setAbonelikler([]);
    })
    .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, appliedBayiFilter]);

  // İlk yüklemede veri çekme - sadece Listele butonuna basınca yüklenecek

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Progress gösterme komponenti
  const ProgressDisplay = ({ firma, imei }) => {
    const abonelik = abonelikler.find(ab => 
      ab.firma === firma || ab.imei === imei
    );

    if (!abonelik || abonelik.progress_yuzdesi === null || abonelik.progress_yuzdesi === undefined) {
      return (
        <Text fontSize="xs" color="gray.500">
          Veri Yok
        </Text>
      );
    }

    const progressValue = Math.max(0, Math.min(100, abonelik.progress_yuzdesi || 0));
    const kalanGun = abonelik.kalan_gun || 0;

    return (
      <VStack spacing={1} align="stretch">
        <Progress 
          value={progressValue}
          colorScheme={kalanGun > 365 ? "green" : kalanGun > 100 ? "yellow" : "red"}
          size="sm"
          borderRadius="md"
          bg="gray.200"
        />
        <HStack justify="space-between">
          <Text fontSize="xs" color="gray.600">
            {kalanGun} gün
          </Text>
          <Text fontSize="xs" color="gray.500">
            %{progressValue.toFixed(0)}
          </Text>
        </HStack>
      </VStack>
    );
  };

  const filteredData = useMemo(() => {
    return accounts
      .filter((r) => {
        // Firma adı filtresi (ID bazlı)
        // "all" seçildiğinde tüm firmalar gösterilir (filtreleme yapılmaz)
        if (appliedFirmaAdiFilter && appliedFirmaAdiFilter !== "all" && String(r.id) !== String(appliedFirmaAdiFilter)) {
          return false;
        }
        // Telefon filtresi
        if (appliedTelefonFilter && !(r.irtibat || "").includes(appliedTelefonFilter)) {
          return false;
        }
        return true;
      })
      .filter((r) => {
        if (!appliedStatusFilter) return true;
        const durum =
          String(r.firma_durumu || r.traccar_status || "")
            .toLowerCase()
            .trim();
        if (appliedStatusFilter === "aktif") {
          return durum === "aktif" || durum === "true" || durum === "1";
        }
        if (appliedStatusFilter === "pasif") {
          return durum === "pasif" || durum === "false" || durum === "0";
        }
        return true;
      })
      .filter((r) => {
        if (!appliedTypeFilter) return true;
        // Backend'den customerType veya musteri_tipi olarak gelebilir
        const type = String(r.musteri_tipi || r.customerType || "").toLowerCase().trim();
        const filterLower = appliedTypeFilter.toLowerCase().trim();
        return type === filterLower;
      })
      .filter((r) => {
        if (!appliedUetdsFilter) return true;
        const u = r.uetds;
        if (appliedUetdsFilter === "acik") return u === true;
        if (appliedUetdsFilter === "kapali") return u === false;
        return true;
      })
      .map((r) => {
      // traccar_status artık backend'de firms tablosundaki firma_durumu'na göre hesaplanıyor
      // "Aktif" ise aktif, "Pasif" ise pasif
      const aktifMi =
        String(r.traccar_status || "").toLowerCase() === "aktif" ||
        r.traccar_status === true ||
        r.traccar_status === "true";
      const notlarView = (() => {
        const val = r.notlar;
        if (!val) return "-";
        const lines = String(val).split(/\n+/).filter(Boolean);
        if (!lines.length) return "-";
        // [NOT] 2026-01-19 14:56 - Test formatından sadece "Test" kısmını al
        const extractNoteContent = (line) => {
          const match = line.match(/\[NOT\].*?-\s*(.+)$/);
          return match ? match[1].trim() : line;
        };
        const show = lines.slice(-3).reverse().map(extractNoteContent); // son 3 kaydı tersten göster
        return (
          <VStack align="start" spacing={0.5} maxW="260px">
            {show.map((line, idx) => (
              <Text key={idx} fontSize="xs" color="gray.700" noOfLines={2}>
                {line}
              </Text>
            ))}
            {lines.length > 3 && (
              <Text fontSize="xx-small" color="gray.400">
                +{lines.length - 3} eski kayıt
              </Text>
            )}
          </VStack>
        );
      })();
      
      const firmaName = r.firma || r.firma_ismi || r.name || "Bilinmiyor";
      return {
        ...r,
        HspId: r.HspId || r.id,
        firma: formatFirmaName(firmaName),
        firma_ismi: formatFirmaName(firmaName),
        adres: r.adres || r.acik_adres || "-",
        traccar_status: aktifMi ? (
          <CheckCircleIcon color="green.500" boxSize={4} />
        ) : (
          <CloseIcon color="red.500" boxSize={3} />
        ),
        uetds:
          r.uetds === true ? (
            <CheckCircleIcon color="green.500" boxSize={4} />
          ) : r.uetds === false ? (
            <CloseIcon color="red.500" boxSize={3} />
          ) : (
            <Text fontSize="xs" color="gray.500">
              Veri yok
            </Text>
          ),
        kayit_tarihi: formatDate(r.kayit_tarihi),
        updatedAt: formatDate(r.upd_tarihi || r.updatedAt),
        _kayit_tarihi_timestamp: r.kayit_tarihi ? new Date(r.kayit_tarihi).getTime() : null,
        _updatedAt_timestamp: (r.upd_tarihi || r.updatedAt) ? new Date(r.upd_tarihi || r.updatedAt).getTime() : null,
        abonelik_durumu: (
          <ProgressDisplay 
            firma={formatFirmaName(firmaName)} 
            imei={r.imei}
          />
          ),
        notlar: notlarView,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accounts,
    abonelikler,
    appliedFirmaAdiFilter,
    appliedTelefonFilter,
    appliedStatusFilter,
    appliedTypeFilter,
    appliedUetdsFilter,
  ]);
  // abonelikler dependency'si ProgressDisplay component'i için gerekli

  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;
    if (sortColumn === "kayit_tarihi" || sortColumn === "updatedAt") {
      const tsKey = sortColumn === "kayit_tarihi" ? "_kayit_tarihi_timestamp" : "_updatedAt_timestamp";
      return [...filteredData].sort((a, b) => {
        const ta = a[tsKey];
        const tb = b[tsKey];
        if (!ta && !tb) return 0;
        if (!ta) return 1;
        if (!tb) return -1;
        return sortOrder === "asc" ? ta - tb : tb - ta;
      });
    }
    return sortTableData(filteredData, sortColumn, sortOrder);
  }, [filteredData, sortColumn, sortOrder]);

  const handleSort = (col) => {
    if (sortColumn === col) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setSortColumn(col);
      setSortOrder("asc");
    }
  };

  const onHesapIslemleri = (firma) => {
    setSelectedFirmaForHesapIslemleri(firma);
    setHesapIslemleriOpen(true);
  };

  const handleContextMenu = (e, row) => {
    e.preventDefault();
    setContext({ position: { x: e.clientX, y: e.clientY }, row });
  };

  const closeMenu = () => setContext({ position: null, row: null });

  const loadBulkCandidates = useCallback(async () => {
    setBulkCandidatesLoading(true);
    try {
      const res = await api.get("/api/firmalar?select_only=true&include_uetds=true");
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Hesap listesi alınamadı");
      }
      setBulkCandidates(Array.isArray(data) ? data : []);
    } catch (err) {
      handleError(err, "Hesap listesi yüklenemedi");
      setBulkCandidates([]);
    } finally {
      setBulkCandidatesLoading(false);
    }
  }, [handleError]);

  const bulkFilteredFirms = useMemo(() => {
    let list = bulkCandidates;
    if (bulkUetdsOnly) {
      list = list.filter((f) => f.uetds === true);
    }
    if (userRole === "admin") {
      if (selectedBayiIds.includes("all")) {
        // tüm hesaplar
      } else if (selectedBayiIds.length > 0) {
        const bayiIdSet = new Set(
          selectedBayiIds.filter((id) => id !== "all").map((id) => Number(id))
        );
        list = list.filter(
          (f) => f.bayi_id != null && bayiIdSet.has(Number(f.bayi_id))
        );
      } else {
        list = list.filter((f) => f.bayi_id == null);
      }
    }
    const normalizedQuery = normalizeSearchText(bulkAccountSearch);
    if (normalizedQuery) {
      list = list.filter((f) => {
        const name = String(f.firma_ismi || f.firma || "");
        const phone = `${f.irtibat || ""} ${f.sabit_no || ""}`;
        return (
          normalizeSearchText(name).includes(normalizedQuery) ||
          normalizeSearchText(phone).includes(normalizedQuery)
        );
      });
    }
    return [...list].sort((a, b) =>
      String(a.firma_ismi || a.firma || "").localeCompare(
        String(b.firma_ismi || b.firma || ""),
        "tr"
      )
    );
  }, [
    bulkCandidates,
    bulkUetdsOnly,
    selectedBayiIds,
    bulkAccountSearch,
    userRole,
  ]);

  const openBulkModal = useCallback(() => {
    setBulkResult(null);
    setSelectedBayiIds([]);
    setBulkUetdsOnly(false);
    setSelectedFirmaIds([]);
    setBulkAccountSearch("");
    setBulkModalOpen(true);
    loadBulkCandidates();
  }, [loadBulkCandidates]);

  const closeBulkModal = () => {
    setBulkModalOpen(false);
    setBulkSending(false);
    setSelectedBayiIds([]);
    setBulkUetdsOnly(false);
    setSelectedFirmaIds([]);
    setBulkAccountSearch("");
    setBulkCandidates([]);
  };

  const toggleSelectAllVisibleBulk = () => {
    const visibleIds = bulkFilteredFirms.map((f) => String(f.id ?? f.HspId));
    const allSelected =
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedFirmaIds.includes(id));
    if (allSelected) {
      setSelectedFirmaIds((prev) =>
        prev.filter((id) => !visibleIds.includes(id))
      );
    } else {
      setSelectedFirmaIds((prev) => [
        ...new Set([...prev, ...visibleIds]),
      ]);
    }
  };

  const handleBulkSend = async () => {
    if (!bulkMessage.trim()) {
      toast({
        title: "Mesaj gerekli",
        description: "Lütfen gönderilecek mesajı yazın.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setBulkSending(true);
    setBulkResult(null);
    try {
      const payload = {
        message: bulkMessage.trim(),
      };
      if (bulkUetdsOnly) {
        payload.uetds_only = true;
      }

      if (selectedFirmaIds.length > 0) {
        payload.firma_ids = selectedFirmaIds.map((id) => parseInt(id, 10));
      } else if (userRole === "admin") {
        if (selectedBayiIds.includes("all")) {
          payload.send_to_all = true;
        } else if (selectedBayiIds.length > 0) {
          payload.bayi_ids = selectedBayiIds
            .filter((id) => id !== "all")
            .map((id) => parseInt(id, 10));
        }
      }

      const res = await api.post("/api/firmalar/bulk-sms", payload);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Toplu SMS gönderilemedi");
      }
      setBulkResult(data);
      toast({
        title: "SMS gönderildi",
        description: `${data.sent} numaraya ulaştı, ${data.failed} başarısız.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "SMS gönderim hatası");
    } finally {
      setBulkSending(false);
    }
  };

  const handleOpenHatirlatmaGonder = async (firma) => {
    const firmaId = firma?.HspId || firma?.id;
    if (!firmaId) {
      toast({
        title: "Firma bulunamadı",
        status: "warning",
      });
      return;
    }
    try {
      const res = await api.get(`/api/abonelikler/firma/${firmaId}/devices`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Firma abonelik bilgisi alınamadı");
      }
      const list = Array.isArray(data) ? data : [];
      if (!list.length) {
        toast({
          title: "Aktif araç bulunamadı",
          description: "Bu firmaya bağlı hatırlatma gönderilecek araç bulunamadı.",
          status: "warning",
        });
        return;
      }
      const hedef = list.find((item) => item?.cihaz_id || item?.device_id) || list[0];
      setHatirlatmaModal({
        isOpen: true,
        abonelik: {
          ...hedef,
          firma: firma?.firma || firma?.firma_ismi || hedef?.firma || "",
          plaka: hedef?.plaka || "-",
        },
      });
    } catch (err) {
      handleError(err, "Hatırlatma modalı açma hatası");
    }
  };

  return (
    <Box position="relative" mt={{ base: 4, md: 8 }}>
      <FilterPanel
        fields={[
          {
            key: "firma_adi",
            type: "searchable-select",
            placeholder: "Firma Seçiniz",
            value: firmaAdiFilter,
            onChange: setFirmaAdiFilter,
            options: [
              { label: "Hepsi", value: "all" }, // "Hepsi" için özel değer
              ...firms.map((firma) => ({
                label: firma.name,
                value: firma.id,
              })),
            ],
            isDisabled: firmsLoading,
          },
          ...(userRole === "admin" ? [{
            key: "bayi",
            type: "select",
            placeholder: "Bayi Seçiniz",
            value: bayiFilter,
            onChange: setBayiFilter,
            options: [
              { label: "Tümü", value: "" },
              { label: "ixPilot", value: "ixpilot" },
              ...bayiler.map((bayi) => ({
                label: bayi.name,
                value: bayi.id,
              })),
            ],
            isDisabled: bayilerLoading,
          }] : []),
          {
            key: "telefon",
            type: "text",
            placeholder: "Telefon",
            value: telefonFilter,
            onChange: setTelefonFilter,
          },
          {
            key: "status",
            type: "select",
            placeholder: "Firma Durumu",
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
              { label: "Aktif", value: "aktif" },
              { label: "Pasif", value: "pasif" },
            ],
          },
          {
            key: "type",
            type: "select",
            placeholder: "Firma Türü",
            value: typeFilter,
            onChange: setTypeFilter,
            options: [
              { label: "Bireysel", value: "Bireysel" },
              { label: "Kurumsal", value: "Kurumsal" },
            ],
          },
          {
            key: "uetds",
            type: "select",
            placeholder: "UETDS",
            value: uetdsFilter,
            onChange: setUetdsFilter,
            options: [
              { label: "Tümü", value: "" },
              { label: "Açık", value: "acik" },
              { label: "Kapalı", value: "kapali" },
            ],
          },
        ]}
        onSearch={() => loadAccounts(true)}
        searchButtonLoading={loading}
        searchButtonLoadingText="Listeleniyor..."
        onReset={() => {
          setFirmaAdiFilter(""); // Reset'te boş string (hiçbir şey seçilmedi)
          setTelefonFilter("");
          setStatusFilter("");
          setTypeFilter("");
          setUetdsFilter("");
          setBayiFilter("");
          setAppliedFirmaAdiFilter("");
          setAppliedTelefonFilter("");
          setAppliedStatusFilter("");
          setAppliedTypeFilter("");
          setAppliedUetdsFilter("");
          setAppliedBayiFilter("");
          setHasSearched(false);
        }}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            loadAccounts(true);
          }
        }}
        actions={useMemo(() => {
          // Teknik personel için butonu gizle
          if (userRole === "teknik_eleman") {
            return [];
          }
          return [
            {
              key: "bulk-notify",
              label: "Toplu bildirim gönder",
              onClick: openBulkModal,
              buttonProps: { colorScheme: "blue", variant: "outline" },
            },
          ];
        }, [userRole, openBulkModal])}
      />

      <DataTable
        title={`Hesaplar • ${sortedData.length} hesap`}
        filterBar={
          <HStack justify="flex-end" spacing={3}>
            {!(userRole === "bayi" && userBayiTipi === "yetkili_bayi") && (
              <Button
                size="sm"
                leftIcon={<FiPlus />}
                colorScheme="blue"
                onClick={() => setEkleOpen(true)}
              >
                Firma Ekle
              </Button>
            )}
            <Button
              size="sm"
              leftIcon={<FiRefreshCw />}
              variant="ghost"
              onClick={() => loadAccounts(false, appliedBayiFilter)}
              isLoading={loading}
              loadingText="Yükleniyor"
              isDisabled={!hasSearched}
            >
              Yenile
            </Button>
          </HStack>
        }
        columns={columns}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        data={sortedData}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onSort={handleSort}
        tableFontSize="12px"
        tableRowHeight="26px"
        loading={loading}
        hasSearched={hasSearched}
        emptyMessage={
          hasSearched
            ? "Arama kriterlerinize uygun kayıt bulunamadı."
            : "Henüz liste yüklenmedi. Filtreleri doldurup 'Listele' butonuna basarak arama yapabilirsiniz."
        }
        onRowDoubleClick={(row) => {
          // Firma ID'sini al (HspId veya id)
          const firmaId = row.HspId || row.id;
          if (firmaId) {
            // State ile firma ID'sini gönder
            history.push({
              pathname: "/admin/Araclar",
              state: { autoSelectFirmaId: String(firmaId) }
            });
          }
        }}
        onRowContextMenu={handleContextMenu}
      />

      {context.position && context.row && (
        <FirmalarContextMenu
          position={context.position}
          data={context.row}
          userRole={userRole}
          onClose={closeMenu}
          onOpenFirmaDetay={(firma) => setSelectedFirma(firma)}
          onAracEkle={(firma) => {
            setSelectedFirmaForArac(firma);
            setAracEkleOpen(true);
          }}
          onHesapIslemleri={onHesapIslemleri}
          onOpenUetds={(firmaRow) => setFirmaUetds(firmaRow)}
          onFirmaPasiflestir={(firma) =>
            setPasifModal({ isOpen: true, firma, reason: "", submitting: false })
          }
          onFirmaAktiflestir={async (firma) => {
            try {
              const res = await api.post(
                `/api/firmalar/${firma?.HspId || firma?.id}/aktiflestir`
              );
              const data = await res.json();
              if (!res.ok) {
                throw new Error(data?.detail || "Firma aktifleştirilemedi");
              }
              toast({
                title: "Firma aktifleştirildi",
                status: "success",
                duration: 3000,
                isClosable: true,
              });
              loadAccounts();
            } catch (err) {
              // Merkezi error handling - Güvenli mesaj göster
              handleError(err, "Firma işlemi hatası");
            }
          }}
          onDegisiklikGecmisi={(firma) => {
            setSelectedFirmaForGecmis(firma);
            setIsDegisiklikGecmisiOpen(true);
          }}
          onNotEkle={(firma) => {
            setSelectedFirmaForNot(firma);
            setIsNotEkleModalOpen(true);
          }}
          onHatirlatmaGonder={handleOpenHatirlatmaGonder}
        />
      )}

      <FirmaDetayModal
        isOpen={!!selectedFirma}
        onClose={() => setSelectedFirma(null)}
        firma={selectedFirma}
        refreshList={loadAccounts}
      />

      <FirmaUetdsModal
        isOpen={Boolean(firmaUetds)}
        onClose={() => setFirmaUetds(null)}
        firma={firmaUetds}
      />

      {/* ✅ Firma ekle modal */}
      <FirmaEkleModal
        isOpen={isEkleOpen}
        onClose={() => setEkleOpen(false)}
        onSave={() => {
          loadAccounts();
          setEkleOpen(false);
        }}
        bayiler={bayiler} // ✅ Bayiler verisini prop olarak geç
      />

      {/* ✅ Araç ekle modal */}
      <AracEkleModal
        isOpen={isAracEkleOpen}
        onClose={() => setAracEkleOpen(false)}
        firmaOptions={accounts.map((r) => r.firma_ismi || r.firma).filter(Boolean)} 
        kilitliFirma={selectedFirmaForArac?.firma_ismi || selectedFirmaForArac?.firma}
        personelOptions={["Ahmet", "Mehmet", "Ali"]}
        onSave={() => {
          setAracEkleOpen(false);
        }}
        bayiler={bayiler} // ✅ Bayiler verisini prop olarak geç
      />

      <HesapIslemleriModal
        isOpen={isHesapIslemleriOpen}
        onClose={() => {
          setHesapIslemleriOpen(false);
          setSelectedFirmaForHesapIslemleri(null);
        }}
        firma={selectedFirmaForHesapIslemleri}
        refreshList={loadAccounts}
      />

      {/* ✅ DEĞİŞİKLİK GEÇMİŞİ MODAL */}
      <DegisiklikGecmisiModal
        isOpen={isDegisiklikGecmisiOpen}
        onClose={() => {
          setIsDegisiklikGecmisiOpen(false);
          setSelectedFirmaForGecmis(null);
        }}
        firma={selectedFirmaForGecmis}
      />

      {/* ✅ NOT EKLE MODAL */}
      <FirmaNotEkleModal
        isOpen={isNotEkleModalOpen}
        onClose={() => {
          setIsNotEkleModalOpen(false);
          setSelectedFirmaForNot(null);
        }}
        firmaData={selectedFirmaForNot}
        onSuccess={(updatedFirma) => {
          // Eğer güncellenmiş firma verisi geldiyse, state'i direkt güncelle
          if (updatedFirma && (updatedFirma.HspId || updatedFirma.id)) {
            setAccounts((prevAccounts) =>
              prevAccounts.map((account) => {
                const accountId = account.HspId || account.id;
                const updatedId = updatedFirma.HspId || updatedFirma.id;
                if (accountId === updatedId) {
                  return {
                    ...account,
                    ...updatedFirma,
                    notlar: updatedFirma.notlar || account.notlar,
                  };
                }
                return account;
              })
            );
          } else {
            // Fallback: Eğer veri gelmediyse liste yenile
            loadAccounts();
          }
          setIsNotEkleModalOpen(false);
          setSelectedFirmaForNot(null);
        }}
      />

      {/* Firma pasifleştir modal */}
      <Modal
        isOpen={pasifModal.isOpen}
        onClose={() => setPasifModal({ isOpen: false, firma: null, reason: "", submitting: false })}
        isCentered
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" overflow="hidden" bg="white" p={1}>
          {/* Header */}
          <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
            <ModalCloseButton size="sm" />
            <VStack spacing={1} align="start">
              <Text fontSize="lg" fontWeight="medium" color="gray.800">
                Firmayı Pasifleştir
              </Text>
              <Text fontSize="xs" color="gray.600">
                {pasifModal.firma?.firma || pasifModal.firma?.firma_ismi}
              </Text>
            </VStack>
          </Box>
          <ModalBody px={6} py={4}>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" color="gray.700">
                Bu firma Traccar üzerinde devre dışı bırakılacak. Lütfen pasifleştirme sebebini yazın.
              </Text>
              <FormControl isRequired>
                <FormLabel fontSize="sm" color="gray.600">Pasifleştirme Sebebi</FormLabel>
                <Textarea
                  variant="flushed"
                  size="sm"
                  placeholder="Sebep girin"
                  value={pasifModal.reason}
                  onChange={(e) => setPasifModal((p) => ({ ...p, reason: e.target.value }))}
                  rows={4}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter bg="white" px={6} py={4}>
            <HStack spacing={3}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setPasifModal({ isOpen: false, firma: null, reason: "", submitting: false })
                }
              >
                Vazgeç
              </Button>
              <Button
                colorScheme="red"
                size="sm"
                isLoading={pasifModal.submitting}
                onClick={async () => {
                  if (!pasifModal.reason.trim()) {
                    toast({
                      title: "Sebep gerekli",
                      description: "Lütfen pasifleştirme sebebi yazın.",
                      status: "warning",
                    });
                    return;
                  }
                  try {
                    setPasifModal((p) => ({ ...p, submitting: true }));
                    const res = await api.post(
                      `/api/firmalar/${pasifModal.firma?.HspId || pasifModal.firma?.id}/pasiflestir`,
                      { sebep: pasifModal.reason.trim() }
                    );
                    const data = await res.json();
                    if (!res.ok) {
                      throw new Error(data?.detail || "Pasifleştirme başarısız");
                    }
                    toast({
                      title: "Firma pasifleştirildi",
                      description: "Traccar'da kullanıcı devre dışı bırakıldı.",
                      status: "success",
                    });
                    setPasifModal({ isOpen: false, firma: null, reason: "", submitting: false });
                    loadAccounts();
                  } catch (err) {
                    // Merkezi error handling - Güvenli mesaj göster
                    handleError(err, "Traccar kullanıcı işlemi hatası");
                    setPasifModal((p) => ({ ...p, submitting: false }));
                  }
                }}
              >
                Pasifleştir
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isBulkModalOpen} onClose={closeBulkModal} size="2xl" isCentered motionPreset="slideInBottom">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" overflow="hidden" bg="white" p={1}>
          {/* Header */}
          <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
            <ModalCloseButton size="sm" />
            <VStack spacing={1} align="start">
              <Text fontSize="lg" fontWeight="medium" color="gray.800">
                Toplu Bildirim Gönder
              </Text>
              <Text fontSize="xs" color="gray.600">
                Hesap seçerseniz yalnızca seçilenlere gider; seçim yoksa bayi / UETDS kurallarına göre toplu gönderilir
              </Text>
            </VStack>
          </Box>
          <ModalBody px={6} py={4} maxH="65vh" overflowY="auto">
            <FormControl isRequired>
              <FormLabel fontSize="sm" color="gray.600">Mesaj</FormLabel>
              <Textarea
                variant="flushed"
                size="sm"
                rows={4}
                placeholder="Gönderilecek mesajı yazın..."
                value={bulkMessage}
                onChange={(e) => setBulkMessage(e.target.value)}
              />
            </FormControl>

            <FormControl mt={3}>
              <Checkbox
                isChecked={bulkUetdsOnly}
                onChange={(e) => setBulkUetdsOnly(e.target.checked)}
                colorScheme="purple"
              >
                <Text as="span" fontSize="sm">
                  Yalnızca UETDS’li hesaplar
                </Text>
              </Checkbox>
              <Text fontSize="xs" color="gray.500" mt={1} ml={6}>
                {selectedFirmaIds.length > 0
                  ? "Seçili hesaplar arasında UETDS açık olanlara gönderilir."
                  : "Hesap seçilmezse gönderimde yalnızca UETDS’li firmalar dahil edilir; listede de bu filtreye uyanlar gösterilir."}
              </Text>
            </FormControl>

            {userRole === "admin" && bayiler && bayiler.length > 0 && (
              <FormControl mt={3}>
                <FormLabel fontSize="sm">Bayiler (opsiyonel)</FormLabel>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  {selectedFirmaIds.length > 0
                    ? "Yalnızca aşağıdaki hesap listesini daraltır."
                    : "Hesap seçilmezse: Hepsi = tüm firmalar, belirli bayi = o bayiler, boş = yalnızca ixPilot hesapları."}
                </Text>
                <Box
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={3}
                  maxH="120px"
                  overflowY="auto"
                >
                  <CheckboxGroup
                    value={selectedBayiIds}
                    onChange={(values) => {
                      if (values.includes("all")) {
                        setSelectedBayiIds(["all"]);
                      } else {
                        setSelectedBayiIds(values.filter((id) => id !== "all"));
                      }
                    }}
                  >
                    <VStack align="stretch" spacing={2}>
                      <Checkbox value="all" fontWeight="semibold">
                        Hepsi
                      </Checkbox>
                      <Divider />
                      {bayiler.map((bayi) => (
                        <Checkbox key={bayi.id} value={String(bayi.id)}>
                          {bayi.name || bayi.username}
                        </Checkbox>
                      ))}
                    </VStack>
                  </CheckboxGroup>
                </Box>
              </FormControl>
            )}

            <FormControl mt={4}>
              <HStack justify="space-between" align="center" mb={2}>
                <FormLabel fontSize="sm" mb={0}>
                  Hesaplar (opsiyonel, {selectedFirmaIds.length} seçili)
                </FormLabel>
                <Button
                  size="xs"
                  variant="link"
                  colorScheme="purple"
                  onClick={toggleSelectAllVisibleBulk}
                  isDisabled={bulkFilteredFirms.length === 0}
                >
                  {bulkFilteredFirms.length > 0 &&
                  bulkFilteredFirms.every((f) =>
                    selectedFirmaIds.includes(String(f.id ?? f.HspId))
                  )
                    ? "Görünenlerin seçimini kaldır"
                    : "Görünenlerin tümünü seç"}
                </Button>
              </HStack>
              <Input
                size="sm"
                placeholder="Ara..."
                value={bulkAccountSearch}
                onChange={(e) => setBulkAccountSearch(e.target.value)}
                mb={1}
              />
              {!bulkCandidatesLoading && bulkCandidates.length > 0 && (
                <Text fontSize="xs" color="gray.500" mb={2}>
                  {bulkFilteredFirms.length} / {bulkCandidates.length} hesap
                  {bulkAccountSearch.trim() ? " (arama uygulandı)" : ""}
                </Text>
              )}
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                maxH="200px"
                overflowY="auto"
                p={2}
                bg="white"
              >
                {bulkCandidatesLoading ? (
                  <HStack justify="center" py={6}>
                    <Spinner size="sm" color="purple.500" />
                    <Text fontSize="sm" color="gray.500">
                      Hesaplar yükleniyor...
                    </Text>
                  </HStack>
                ) : bulkFilteredFirms.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" py={4} textAlign="center">
                    {bulkCandidates.length === 0
                      ? "Gösterilecek hesap bulunamadı."
                      : "Filtreye uyan hesap yok. UETDS veya bayi filtresini kontrol edin."}
                  </Text>
                ) : (
                  <CheckboxGroup
                    value={selectedFirmaIds}
                    onChange={setSelectedFirmaIds}
                  >
                    <VStack align="stretch" spacing={1}>
                      {bulkFilteredFirms.map((f) => {
                        const fid = String(f.id ?? f.HspId);
                        const phone =
                          (f.irtibat || "").trim() ||
                          (f.sabit_no || "").trim() ||
                          "—";
                        return (
                          <Checkbox
                            key={fid}
                            value={fid}
                            size="sm"
                            py={1.5}
                            px={1}
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            bg="white"
                            borderRadius="sm"
                            width="100%"
                          >
                            <HStack
                              spacing={2}
                              flex="1"
                              minW={0}
                              justify="space-between"
                            >
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                noOfLines={1}
                                flex="1"
                                minW={0}
                                title={f.firma_ismi || f.firma}
                              >
                                {f.firma_ismi || f.firma}
                              </Text>
                              <HStack spacing={2} flexShrink={0}>
                                <Text fontSize="xs" color="gray.500">
                                  {phone}
                                </Text>
                                {f.uetds === true && (
                                  <Text
                                    fontSize="xx-small"
                                    color="green.600"
                                    fontWeight="semibold"
                                  >
                                    UETDS
                                  </Text>
                                )}
                              </HStack>
                            </HStack>
                          </Checkbox>
                        );
                      })}
                    </VStack>
                  </CheckboxGroup>
                )}
              </Box>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {selectedFirmaIds.length > 0
                  ? "Mesaj yalnızca işaretli hesaplara gider."
                  : "Hiç hesap işaretlemezseniz mesaj, yukarıdaki bayi ve UETDS kurallarına göre ilgili tüm hesaplara gider."}
              </Text>
            </FormControl>

            {bulkResult && (
              <Box
                mt={4}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                p={3}
                bg="gray.50"
              >
                <Text fontWeight="semibold" mb={2}>
                  Gönderim Özeti
                </Text>
                <VStack align="stretch" spacing={1} fontSize="sm">
                  <HStack justify="space-between">
                    <Text>Toplam firma:</Text>
                    <Text fontWeight="medium">{bulkResult.total_firms}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>Ulaşılan numara:</Text>
                    <Text fontWeight="medium">{bulkResult.recipient_count}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>Başarılı:</Text>
                    <Text color="green.600" fontWeight="medium">
                      {bulkResult.sent}
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>Başarısız:</Text>
                    <Text color="red.600" fontWeight="medium">
                      {bulkResult.failed}
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text>Numarası olmayan firma:</Text>
                    <Text fontWeight="medium">{bulkResult.skipped}</Text>
                  </HStack>
                </VStack>
                <Divider my={3} />
                <Text fontSize="xs" color="gray.500">
                  Ayrıntılı kayıt için NetGSM panelini veya logları kontrol edin.
                </Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter bg="white" px={6} py={4}>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={closeBulkModal} size="sm">
                Kapat
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleBulkSend}
                isLoading={bulkSending}
                loadingText="Gönderiliyor"
                size="sm"
              >
                Gönder
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <HatirlatmaGonderModal
        isOpen={hatirlatmaModal.isOpen}
        onClose={() => setHatirlatmaModal({ isOpen: false, abonelik: null })}
        abonelik={hatirlatmaModal.abonelik}
      />
    </Box>
  );
}